import _ from "lodash";

// third party libraries
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";
import moment from "moment";

// Services
import {
  getAIResponse,
  updateAIConversation,
  getTextToSpeechAudio,
  getSpeechToTextTranscript,
} from "modules/Services";
import { Sentry } from "services/sentry";

// Modules
import {
  getAITutorConversation,
  getAITutorConversationFromCache,
  writeAITutorConversationInCache,
} from "./AITutorChatGraphqlHelpers";
import {
  createAITutorConversationMutation,
  createAITutorMessageMutation,
  createBulkAiMessagesMutation,
  resetAITutorConversationMutation,
  updateAIMessageMutation,
  updateAITutorMessageMutation,
} from "./AITutorChatMutations";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getUserBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  getCurrentCurriculumProgramType,
  getIsStudent,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import {
  getAITutorConversationQuery,
  getMultiAttachmentsQuery,
} from "./AITutorChatQueries";
import {
  bulkHandleAssessmentToolResponse,
  updateAnalysisToolDetails,
} from "modules/AssessmentTool/AssessmentToolModule";
import { getAIAnalysisScaleEvaluation } from "AppComponents/AITutorAnalysis/modules/AITutorAnalysisGraphqlHelpers";
import {
  addAssignmentActivity,
  updateEvaluationData,
} from "ClassRoom/modules/ClassRoomModule";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import {
  getStudentAssignmentDetailsQuery,
  getStudentAssignmentQuery,
} from "ClassRoom/modules/ClassRoomQuery";

// Utils
import { generateRandomId } from "Utils";
import {
  getIsAITutorEnhancementsV2Enabled,
  getIsAITutorModalitiesEnabled,
} from "Utils/ACLStoreUtils";
import {
  getAIAnalysisItem,
  getToolResponseUserFilter,
  getIsStudentAssignmentRevampEnabled,
} from "ClassRoom/modules/utils";
import {
  findEvaluatedCriteriaId,
  getAssessmentFieldValue,
  getAssessmentLearningGoalsContext,
  replaceImgWithInlineLatex,
  mergeAndFilterAITutorMessages,
  AI_TUTOR_TOOL_NAME_STATE_MAPPING,
} from "Utils/AIUtils";
import {
  checkIsThinkingState,
  extractEvaluationAnalysisLevels,
  isMessageFlagged,
  isPlayMode,
  parseProficiencyLevelConversation,
  removeCommasBetweenBraces,
  decodeHTMLEntities,
  extractStateAndContent,
} from "./Utils";

// Constants
import {
  STREAMING,
  GENERATING_EVALUATION,
  THINKING,
} from "Constants/stringConstants";
import { AI_TUTOR_TTS_MODEL, ATTACHMENT_PICK_KEYS } from "./Constants";
import { getOveriddenModelForWhiteboardModality } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/Utils";

export let cancelRequest;
export let cancelTTSRequest;

export const getMultiAttachments = async attachmentIds => {
  try {
    const response = await client.query({
      query: getMultiAttachmentsQuery,
      variables: { ids: attachmentIds },
      fetchPolicy: "network-only",
    });

    return _.get(response, "data.multiNode", {});
  } catch {
    return null;
  }
};

export const createAITutorMessage = params => {
  const {
    variables,
    attachments = null,
    setCurrentAIState,
    writeToCache = true,
    refetchQueries = [],
  } = params;

  return async dispatch => {
    const conversation = getAITutorConversationFromCache({
      id: _.get(variables, "input.conversationId"),
    });

    const userInfo = getUserInfo();

    const newCreateAiMessage = {
      isSuccess: true,
      response: {
        id: generateRandomId(),
        role: null,
        severity: null,
        authorType: _.get(variables, "input.authorType"),
        content: _.get(variables, "input.content"),
        metadata: _.get(variables, "input.metadata"),
        attachments,
        createdBy: {
          ...getUserBasicDetailsFromCache({
            id: userInfo.id,
            type: userInfo.userEntityType,
          }),
          type: userInfo.user_type,
          schoolTenures: [],
        },
        votes: {
          myVote: null,
          __typename: "VoteConnection",
        },
        createdAt: moment().toISOString(),
        usageMetricsId: null,
        parentMessage: {
          id: null,
          __typename: "AiMessage",
        },
        __typename: "AiMessage",
      },
      __typename: "GenericAiMessageResponse",
    };

    try {
      return await client.mutate({
        mutation: createAITutorMessageMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            createAiMessage: newCreateAiMessage,
          },
        },
        update: (
          cache,
          {
            data: {
              ai: { createAiMessage },
            },
          }
        ) => {
          cache.updateQuery(
            {
              query: getAITutorConversationQuery,
              variables: {
                id: _.get(variables, "input.conversationId"),
              },
            },
            data => {
              if (writeToCache) {
                setCurrentAIState?.(THINKING);

                const createAiMessageNode = {
                  node: _.get(createAiMessage, "response"),
                  __typename: "AiMessageEdge",
                };

                if (data) {
                  return update(data, {
                    node: {
                      messages: {
                        edges: {
                          $unshift: [createAiMessageNode],
                        },
                        totalCount: {
                          $set:
                            _.get(conversation, "messages.totalCount", 0) + 1,
                        },
                      },
                    },
                  });
                }
              }

              return data;
            }
          );
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
    } catch (e) {
      cache.updateQuery(
        {
          query: getAITutorConversationQuery,
          variables: {
            id: _.get(variables, "input.conversationId"),
          },
        },
        () => ({ node: conversation })
      );

      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

const _saveMessageTranscripts = async data => {
  const {
    messages,
    dispatch,
    entityId,
    entitySubType,
    userMessageTranscriptsByMainModel = [],
  } = data;
  if (!_.isEmpty(userMessageTranscriptsByMainModel)) {
    _.forEach(messages, message => {
      dispatch(
        updateAITutorMessage({
          variables: {
            input: {
              id: _.get(message, "id"),
              metadata: {
                ..._.get(message, "metadata", {}),
                messageAudioTranscripts: userMessageTranscriptsByMainModel,
              },
            },
          },
        })
      );
    });
    return;
  }

  _.forEach(messages, message => {
    const transcriptPromises = [];
    _.forEach(_.get(message, "metadata.audioUrls", []), audioFileUrl => {
      transcriptPromises.push(
        dispatch(
          getSpeechToTextTranscript({
            entityId,
            entityType: entitySubType,
            audioFileUrl,
          })
        )
      );
    });

    Promise.all(transcriptPromises).then(transcriptResponses => {
      const messageAudioTranscripts = _.compact(
        _.map(transcriptResponses, response =>
          _.get(response, "transcript", "")
        )
      );

      if (!_.isEmpty(messageAudioTranscripts)) {
        dispatch(
          updateAITutorMessage({
            variables: {
              input: {
                id: _.get(message, "id"),
                metadata: {
                  ..._.get(message, "metadata", {}),
                  messageAudioTranscripts,
                },
              },
            },
          })
        );
      }
    });
  });
};

const processToolResponse = async params => {
  const { parsedMessages } = params;

  const result = {};

  const toolResponses = _.map(
    _.filter(parsedMessages, item => item.message?.role === "tool"),
    "message.toolResponse"
  );

  if (!_.isEmpty(toolResponses)) {
    for (const toolResponse of toolResponses) {
      try {
        const parsedToolResponse = JSON.parse(toolResponse || null);
        const { isSuccess, response } = parsedToolResponse || {};

        if (isSuccess) {
          if (_.has(response, "attachmentId")) {
            const attachments = await getMultiAttachments([
              response.attachmentId,
            ]);

            if (!_.isEmpty(attachments)) {
              result.attachments = _.map(attachments, ({ id, type }) => ({
                id,
                type,
              }));
            }
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  return result;
};

const _hasWhiteboardModality = openingMessageResource => {
  const attachmentGroups = _.get(
    openingMessageResource,
    "attachmentGroups",
    []
  );
  const isWorkbookPresent = _.find(attachmentGroups, item => {
    const attachments = _.get(item, "attachments", []);
    const workbookAttachment = _.find(attachments, { type: "WORKBOOK" });
    return !_.isEmpty(workbookAttachment);
  });

  return isWorkbookPresent;
};

export const onSendQuery = params => {
  const {
    t,
    query,
    setQuery,
    entityId,
    aiConversationId,
    course,
    entityType,
    gradeId,
    assessmentDetails,
    attachments,
    attachmentItems,
    setAttachmentItems,
    audioTranscripts,
    setAudioTranscripts,
    updateIsAnalysing,
    updateCurrentAIState,
    selectedTab,
    source,
    setIsEvaluating,
    flaggedMessagesCount,
    learningCourseId,
    setCurrentlyAudioPlayingForItemId,
    userMessageInput,
    skipAssistantMessage,
    ttsAudioRegistry,
    isMountedRef,
    assistantMetadata = {},
    sendButtonParams = {},
  } = params;

  const userQuery = replaceImgWithInlineLatex({ query, attachmentItems });

  setQuery?.("");
  setAttachmentItems?.({});
  setAudioTranscripts?.([]);

  return async (dispatch, getState) => {
    const isTutorSpeakingOnly =
      getAssessmentFieldValue({
        fields: assessmentDetails?.allFields,
        uid: "tutorCommunicationMode",
      }) === "speaking_only";

    try {
      const state = getState();

      const curriculumProgramId = getSelectedCurriculumId(state);
      const curriculumProgramType = getCurrentCurriculumProgramType(state);

      // Check if conversation is empty and create opening message if needed
      let conversation = getAITutorConversationFromCache({
        id: aiConversationId,
      });

      if (_.isEmpty(conversation)) {
        conversation = await getAITutorConversation({
          id: aiConversationId,
        });
      }

      const conversationMessages = _.get(conversation, "messages.edges", []);
      const isConversationEmpty = _.isEmpty(conversationMessages);

      // If conversation is empty, create opening message first
      if (isConversationEmpty) {
        const openingMessage = _.trim(
          _.find(assessmentDetails?.allFields, {
            uid: "openingMessage",
          })?.value
        );

        // Create the opening message if it exists
        if (!_.isEmpty(openingMessage)) {
          let _content = openingMessage;

          if (getIsAITutorEnhancementsV2Enabled()) {
            _content = replaceImgWithInlineLatex({
              query: _content,
              preserveHtml: true,
            });
          }

          await dispatch(
            createAITutorMessage({
              variables: {
                input: {
                  conversationId: aiConversationId,
                  authorType: "assistant",
                  content: _content,
                  metadata: {
                    isOpeningMessage: true,
                  },
                },
              },
              writeToCache: true,
            })
          );
        }
      }

      const updatedAttachments = _.reduce(
        attachments,
        (acc, attachment) => {
          const result = {
            id: generateRandomId(),
            mimeType: null,
            thumbUrl: null,
            title: null,
            metadata: null,
            createdBy: null,
            streamUrl: null,
            signedUrl: null,
            url: null,
            parentType: "AI_MESSAGE",
            createdAt: null,
            presetAttachment: null,
            modalityAttachment: _.get(attachment, "modalityAttachment") || null,
            ..._.pick(attachment, ATTACHMENT_PICK_KEYS),
            __typename: "Attachment",
          };

          acc.push(result);
          return acc;
        },
        []
      );

      // 1. create a message from user input

      const userMessageMetadata = {};

      const imageUrls = _.map(
        _.filter(
          updatedAttachments,
          ({ type, url }) => type === "IMAGE" && !_.startsWith(url, "blob:")
        ),
        "url"
      );

      const audioUrls = _.map(
        _.filter(
          updatedAttachments,
          ({ type, url }) => type === "AUDIO" && !_.startsWith(url, "blob:")
        ),
        "url"
      );

      if (!_.isEmpty(imageUrls)) {
        _.set(userMessageMetadata, "imageUrls", imageUrls);
      }

      const isAITutorEnhancementsV2Enabled = getIsAITutorEnhancementsV2Enabled();
      const isAITutorModalitiesEnabled = getIsAITutorModalitiesEnabled();

      if (isAITutorEnhancementsV2Enabled) {
        if (!_.isEmpty(audioUrls)) {
          _.set(userMessageMetadata, "audioUrls", audioUrls);
        } else if (!_.isEmpty(audioTranscripts)) {
          _.set(userMessageMetadata, "audioTranscripts", audioTranscripts);
        }
      } else {
        if (!_.isEmpty(audioTranscripts) && _.isEmpty(_.trim(userQuery))) {
          _.set(userMessageMetadata, "audioTranscripts", audioTranscripts);
        }
      }

      const processedAttachments = _.map(attachments, attachment => {
        const omitKeys = [];
        let processedAttachment = attachment;

        if (_.startsWith(attachment?.id, "NEW_")) {
          const originalAttachmentId = attachment?.originalAttachmentId;
          if (
            originalAttachmentId &&
            !_.startsWith(originalAttachmentId, "NEW_")
          ) {
            processedAttachment = {
              ...attachment,
              id: originalAttachmentId,
            };
          } else {
            omitKeys.push("id");
          }
        }

        return _.pick(
          _.omit(processedAttachment, omitKeys),
          ATTACHMENT_PICK_KEYS
        );
      });

      let userMessages = [];

      if (_.isArray(userMessageInput)) {
        // Array: do not create default message
        userMessages = _.map(userMessageInput, message => {
          const { authorType } = message || {};
          let { content } = message || {};

          if (authorType === "user" && content) {
            content = _.trim(
              decodeHTMLEntities(replaceImgWithInlineLatex({ query: content }))
            );
          }

          return {
            ...message,
            content,
            attachments: processedAttachments,
          };
        });
      } else if (_.isObject(userMessageInput)) {
        // Object: create default message after the object
        if (isAITutorModalitiesEnabled) {
          userMessages = [
            { ...userMessageInput, attachments: processedAttachments },
          ];

          const content = _.trim(decodeHTMLEntities(userQuery));

          if (!_.isEmpty(content) || !_.isEmpty(processedAttachments)) {
            userMessages.unshift({
              authorType: "user",
              content: content,
              metadata: userMessageMetadata,
              attachments: processedAttachments,
            });
          }
        } else {
          userMessages = [
            {
              authorType: "user",
              content: _.trim(decodeHTMLEntities(userQuery)),
              metadata: userMessageMetadata,
              attachments: processedAttachments,
            },
            { ...userMessageInput, attachments: processedAttachments },
          ];
        }
      } else {
        // Empty/falsy: create only default message
        if (isAITutorModalitiesEnabled) {
          const content = _.trim(decodeHTMLEntities(userQuery));

          if (!_.isEmpty(content) || !_.isEmpty(processedAttachments)) {
            userMessages.unshift({
              authorType: "user",
              content: content,
              metadata: userMessageMetadata,
              attachments: processedAttachments,
            });
          }
        } else {
          userMessages = [
            {
              authorType: "user",
              content: _.trim(decodeHTMLEntities(userQuery)),
              metadata: userMessageMetadata,
              attachments: processedAttachments,
            },
          ];
        }
      }

      if (!skipAssistantMessage) updateCurrentAIState(selectedTab, THINKING);

      let userMessageResponse = null;
      const createUserMessage = _.get(
        sendButtonParams,
        "createUserMessage",
        true
      );

      if (!_.isEmpty(userMessages) && createUserMessage) {
        userMessageResponse = await dispatch(
          createBulkAiMessages({
            input: {
              conversationId: aiConversationId,
              messages: userMessages,
            },
            attachments: updatedAttachments,
          })
        );
      }

      if (createUserMessage && (!userMessageResponse || skipAssistantMessage)) {
        return;
      }

      // 2. generate AI response
      conversation = getAITutorConversationFromCache({
        id: aiConversationId,
      });

      if (_.isEmpty(conversation)) {
        conversation = await getAITutorConversation({
          id: aiConversationId,
        });
      }

      const isLastMessageFlagged = _.some(
        conversation?.messages?.edges,
        message => isMessageFlagged(message)
      );

      let shouldIncreaseFlagCount = false;

      // context calculation
      const grades =
        _.get(course?.primaryGrade, "name") ||
        _.join(_.map(course?.grades, "name"), ", ");

      const aiConversation = _.get(assessmentDetails, "aiConversation.edges");
      const taskCreationConversation = _.find(aiConversation, ({ node }) => {
        return node?.entitySubType === "TASK_CREATION";
      })?.node;

      const isStudent = getIsStudent();

      const createBulkAiMessagesResponse = _.get(
        userMessageResponse,
        "data.ai.createBulkAiMessages.response"
      );

      const aiMessageId = _.find(
        createBulkAiMessagesResponse,
        response =>
          response?.authorType === "user" && !response?.metadata?.isMeta
      )?.id;

      const learningGoalsContext = getAssessmentLearningGoalsContext({
        assessmentDetails,
        curriculumProgramType,
      });

      const context = {
        inputText: userQuery,
        grades,
        ...(params.context || {}),
        ...learningGoalsContext,
      };

      const savedToolUseIds = [];
      const id = generateRandomId();
      const createdAt = moment().toISOString();

      const parentMessageId =
        aiMessageId || _.get(sendButtonParams, "userMessageId", null);

      // Add a promise array to track mutations from the streaming phase
      const messagesInput = [];
      let responseContent = {},
        isFlaggedMessageCreated = false;

      const openingMessageResource = _.get(
        _.find(assessmentDetails?.allFields, {
          uid: "openingMessageResource",
        }),
        "resolvedMinimalTree",
        []
      );

      const learningTool = _hasWhiteboardModality(openingMessageResource)
        ? "create_template_multipage"
        : "";
      const modelSettings = getOveriddenModelForWhiteboardModality(
        learningTool
      );

      const response = await dispatch(
        getAIResponse({
          useStreamParserV2: true,
          variables: {
            input: {
              ...modelSettings,
              action: "ai_tutor_conversation",
              context,
              entityId,
              entityType,
              curriculumProgramId,
              metadata: {
                gradeId,
                assessmentId: assessmentDetails?.id,
                conversationId: aiConversationId,
                attachmentParentId: _.get(taskCreationConversation, "id"),
                imageUrls,
                audioUrls: _.get(userMessageMetadata, "audioUrls"),
                audioTranscripts: _.get(
                  userMessageMetadata,
                  "audioTranscripts"
                ),
                aiMessageId,
                learningCourseId,
              },
              shouldProcessAttachments:
                !_.isEmpty(attachments) ||
                !_.isEmpty(taskCreationConversation?.attachments),
            },
          },
          getCancelToken: token => (cancelRequest = token),
          onDownloadProgress: async params => {
            const {
              content: rawContent,
              usageMetricsId,
              userInputFlagged,
              latestChunkStatus,
              toolCalls,
              parsedMessages,
            } = params || {};

            // when user cancels the request, responseContent will contain the response generated till that point
            responseContent = params;

            // Clean LaTeX content by removing commas between curly braces
            const content = removeCommasBetweenBraces(rawContent);

            /**
              Flagged message handling: When two user messages are flagged, the conversation will be terminated.
             
              Details:
              - When a message is flagged, the backend updates the user message metadata with a flagged category (does not return it though in the mutation response as flagging takes place after the message is saved)
              - Since we don't refetch the conversation, we keep track of flagged messages using flaggedMessagesCount
             
              - If a user had one flagged message in a session, left, and returned later then flaggedMessagesCount will reset to 0
              - But in this case isLastMessageFlagged will be true (as data will be fetched from the network and now message.metadata will have the flagged category)
             
              - Hence isLastMessageFlagged (to track flagging between multiple sessions) and flaggedMessagesCount.current === 1 (to track in a single session) both are required
             */

            // if any of the previous message was flagged
            if (isLastMessageFlagged || flaggedMessagesCount.current === 1) {
              const isCurrentMessageFlagged = isMessageFlagged(toolCalls);

              if (isCurrentMessageFlagged) {
                // hide writing... text
                updateCurrentAIState(selectedTab, "");

                // cancel network request (stop streaming)
                cancelRequest?.();

                // show message that conversation ended to due to repeated inappropriate messages
                await dispatch(
                  createAITutorMessage({
                    variables: {
                      input: {
                        conversationId: aiConversationId,
                        authorType: "flaggedMessage",
                        content:
                          "ai:conversation_ended_due_to_repeated_inappropriate_messages",
                        metadata: {},
                        parentMessageId,
                      },
                    },
                  })
                );

                if (
                  isStudent ||
                  _.includes(["STUDENT_PREVIEW", "PREVIEW_CHAT"], source)
                ) {
                  // we show evaluation in a message for play mode
                  const shouldCreateEvaluationMessage =
                    source === "PREVIEW_CHAT" && isPlayMode(selectedTab);

                  if (shouldCreateEvaluationMessage) setIsEvaluating?.(true);

                  // evaluate conversation
                  await dispatch(
                    onEndSession({
                      course,
                      entityId,
                      aiConversationId,
                      assessmentDetails,
                      shouldCreateEvaluationMessage,
                      updateCurrentAIState: value => {
                        updateCurrentAIState(selectedTab, value);
                      },
                      learningCourseId,
                    })
                  );

                  if (shouldCreateEvaluationMessage) setIsEvaluating?.(false);
                }

                isFlaggedMessageCreated = true;
                return;
              }
            } else {
              if (!shouldIncreaseFlagCount && isMessageFlagged(toolCalls)) {
                shouldIncreaseFlagCount = true;
              }
            }
            /*
            
            when toolUseResponse and toolResponseMessage are not empty, then we need to generate messages like below:
              [
                {
                  role: "assistant",
                  content: "content first chunk",
                  metadata: { toolUseResponse: {...} },
                  parentId: lastUserMessageId
                },
                {
                  role: "user",
                  content: "",
                  metadata: { toolResponseMessage: {...} },
                },
                {
                  role: "assistant",
                  content: "content second chunk",
                  metadata: {},
                  parentId: lastUserMessageId
                },
              ];
            
            */

            if (!_.isEmpty(parsedMessages)) {
              const toolUseIds = _.chain(parsedMessages)
                .map("message.toolCallId")
                .compact()
                .value();

              if (!_.isEqual(toolUseIds, savedToolUseIds)) {
                const flatMessages = _.chain(parsedMessages)
                  .filter(item => _.has(item, "message"))
                  .map("message")
                  .value();

                const newToolUseIds = _.difference(toolUseIds, savedToolUseIds);

                // iterate over each new tool call
                _.forEach(newToolUseIds, toolUseId => {
                  // find the assistant message that triggered the tool call
                  const assistantToolCallMessage = _.find(
                    flatMessages,
                    item => {
                      const toolCallIds = _.map(item?.toolCalls, "id");
                      return _.includes(toolCallIds, toolUseId);
                    }
                  );

                  const { toolCalls } = assistantToolCallMessage || {};

                  const toolResponseParsedMessage = _.find(
                    flatMessages,
                    item => {
                      const { role, toolCallId } = item || {};
                      const isTool = role === "tool";
                      return isTool && toolCallId === toolUseId;
                    }
                  );

                  const toolCallId = toolResponseParsedMessage?.toolCallId;

                  if (!_.isEmpty(toolCallId)) {
                    messagesInput.push(
                      {
                        authorType: "assistant",
                        content: "",
                        usageMetricsId,
                        metadata: {
                          toolCalls: _.filter(toolCalls, {
                            id: toolCallId,
                          }),
                        },
                        parentMessageId,
                      },
                      {
                        authorType: "tool",
                        content: toolResponseParsedMessage?.toolResponse,
                        metadata: {
                          toolCallId: toolCallId,
                        },
                        parentMessageId,
                      }
                    );
                  }

                  // add toolUseId to savedToolUseIds
                  if (!_.includes(savedToolUseIds, toolUseId)) {
                    savedToolUseIds.push(toolUseId);
                  }
                });
              }
            }

            const _content = userInputFlagged
              ? t("common:user_input_flagged_msg")
              : content;

            const createAiMessageNode = {
              node: {
                id,
                role: null,
                severity: null,
                authorType: "assistant",
                content: _content,
                metadata: assistantMetadata || {},
                createdBy: null,
                attachments: null,
                votes: {
                  myVote: null,
                  __typename: "VoteConnection",
                },
                createdAt,
                usageMetricsId: usageMetricsId || null,
                parentMessage: {
                  id: null,
                  __typename: "AiMessage",
                },
                __typename: "AiMessage",
              },
              __typename: "AiMessageEdge",
            };

            const dataToSend = update(conversation, {
              messages: {
                edges: {
                  $unshift: [createAiMessageNode],
                },
                totalCount: {
                  $set: _.get(conversation, "messages.totalCount", 0) + 1,
                },
              },
            });

            if (
              isAITutorModalitiesEnabled &&
              latestChunkStatus === "TOOL_INDICATION"
            ) {
              const latestToolCall = _.chain(toolCalls)
                .last()
                .get("function.name")
                .value();

              const newState = _.get(
                AI_TUTOR_TOOL_NAME_STATE_MAPPING,
                latestToolCall,
                THINKING
              );

              updateCurrentAIState(selectedTab, newState);
              return;
            }

            if (!_.isEmpty(_content)) {
              writeAITutorConversationInCache({
                variables: {
                  id: aiConversationId,
                },
                data: {
                  node: dataToSend,
                },
              });

              if (latestChunkStatus === "TOOL_INDICATION") {
                updateIsAnalysing(selectedTab, true);
              } else {
                updateIsAnalysing(selectedTab, false);

                if (
                  checkIsThinkingState([createAiMessageNode]) ||
                  // keep on showing thinking for audio only, because audio can only play when complete response is generated from AI
                  isTutorSpeakingOnly
                ) {
                  updateCurrentAIState(selectedTab, THINKING);
                } else {
                  updateCurrentAIState(selectedTab, STREAMING);
                }
              }
            }
          },
        })
      );

      if (shouldIncreaseFlagCount) flaggedMessagesCount.current += 1;

      // 3. create a message of the generated response from AI
      const {
        content: aiResponse,
        usageMetricsId,
        userInputFlagged = false,
        parsedMessages,
      } = response || responseContent || {};

      if (
        !isFlaggedMessageCreated &&
        (!_.isEmpty(aiResponse) || userInputFlagged)
      ) {
        // Clean LaTeX content by removing commas between curly braces
        const cleanedResponse = removeCommasBetweenBraces(aiResponse);

        let content;
        if (userInputFlagged) {
          content = t("common:user_input_flagged_msg");
        } else {
          content = cleanedResponse;
        }

        content = decodeHTMLEntities(content);

        const metadata = {
          userInputFlagged,
          isInterrupted: !_.isEmpty(responseContent) && _.isEmpty(response),
          ...assistantMetadata,
        };

        const assistantMessageExtraParams = {};

        const result = await processToolResponse({
          parsedMessages,
        });

        if (!_.isEmpty(result.attachments)) {
          assistantMessageExtraParams.attachments = result.attachments;
        }

        messagesInput.push({
          authorType: "assistant",
          content,
          usageMetricsId,
          metadata,
          parentMessageId,
          ...assistantMessageExtraParams,
        });

        const bulkResponse = await dispatch(
          createBulkAiMessages({
            input: {
              conversationId: aiConversationId,
              messages: messagesInput,
            },
          })
        );

        const allMessages = _.get(
          bulkResponse,
          "data.ai.createBulkAiMessages.response",
          []
        );

        const mergedMessages = mergeAndFilterAITutorMessages(
          _.map(allMessages, node => ({ node }))
        );

        const messageForAudio = _.get(
          _.find(mergedMessages, ({ node } = {}) => {
            const { authorType, content } = node || {};
            return authorType === "assistant" && !_.isEmpty(content);
          }),
          "node",
          {}
        );

        const audioMessageId = messageForAudio?.id;

        let userMessageTranscriptsByMainModel = [];

        if (isTutorSpeakingOnly && !userInputFlagged) {
          // Text-to-Speech (TTS) handling for speaking-only mode
          // This block converts the AI response text to streamed audio playback

          // Extract content and TTS instructions from the AI response
          // extractStateAndContent parses special tags like <output> and <instructions> from the content
          const {
            content: extractedContent,
            output: outputTagContent,
            instructions: extendedTTSInstructions,
            transcript: userMessageTranscript,
          } = extractStateAndContent(content) || {};
          const cleanedContent = extractedContent || outputTagContent;

          if (!_.isEmpty(userMessageTranscript)) {
            userMessageTranscriptsByMainModel = [userMessageTranscript];
          }

          // Combine task-level TTS instructions with any extended instructions from the AI response
          const TTSInstructions = _.trim(
            `${_.get(
              taskCreationConversation,
              "metadata.instructionsForTTSModel",
              ""
            )} \n ${extendedTTSInstructions || ""}`
          );

          // Only create audio if we have a valid message ID and it's not already being processed
          if (audioMessageId && !ttsAudioRegistry.has(audioMessageId)) {
            // Check if component is still mounted before creating audio element
            if (!isMountedRef?.current) {
              // Component unmounted, but still fetch audio URL for persistence
              const streamResponse = await dispatch(
                getTextToSpeechAudio({
                  inputText: cleanedContent,
                  entityType: "AI_TUTOR_MESSAGE",
                  entityId: audioMessageId,
                  model: AI_TUTOR_TTS_MODEL,
                  ...(!_.isEmpty(TTSInstructions)
                    ? { instructions: TTSInstructions }
                    : {}),
                })
              );

              // Save the audio URL even though component is unmounted
              const finalAudioUrl = _.get(streamResponse, "speechUrl");
              if (finalAudioUrl && messageForAudio?.id) {
                await dispatch(
                  updateAITutorMessage({
                    variables: {
                      input: {
                        id: messageForAudio.id,
                        metadata: {
                          ..._.get(messageForAudio, "metadata", {}),
                          audioUrl: finalAudioUrl,
                          instructionsForTTSModel: TTSInstructions,
                        },
                      },
                    },
                  })
                );
              }
            } else {
              // Component is mounted, proceed with audio playback
              // Create HTML5 Audio element with autoplay enabled for immediate playback
              const audioEl = new Audio();
              audioEl.autoplay = true;

              // Use MediaSource API for streaming audio playback
              // This allows audio to play while chunks are still being received
              const mediaSource = new MediaSource();
              audioEl.src = URL.createObjectURL(mediaSource);

              // Register this audio in the TTS registry to track its state
              ttsAudioRegistry.set(audioMessageId, {
                audioEl,
                mediaSource,
                sourceBuffer: null,
                hasError: false,
                isReady: false,
                hasEnded: false,
                isCancelled: false,
              });

              // When audio starts playing, update UI state to indicate playback
              audioEl.addEventListener(
                "playing",
                () => {
                  // Check if still mounted before updating state
                  if (!isMountedRef?.current) {
                    audioEl.pause();
                    audioEl.muted = true;
                    return;
                  }
                  setCurrentlyAudioPlayingForItemId?.(audioMessageId);
                  updateCurrentAIState(selectedTab, "");
                },
                { once: true }
              );

              // Handle MediaSource initialization when it's ready to receive data
              mediaSource.addEventListener("sourceopen", async () => {
                // Check if still mounted before processing audio
                if (!isMountedRef?.current) {
                  audioEl.pause();
                  audioEl.muted = true;
                  if (mediaSource.readyState === "open") {
                    try {
                      mediaSource.endOfStream();
                    } catch {}
                  }

                  // Still fetch and save audio URL even though playback is cancelled
                  const streamResponse = await dispatch(
                    getTextToSpeechAudio({
                      inputText: cleanedContent,
                      entityType: "AI_TUTOR_MESSAGE",
                      entityId: audioMessageId,
                      model: AI_TUTOR_TTS_MODEL,
                      ...(!_.isEmpty(TTSInstructions)
                        ? { instructions: TTSInstructions }
                        : {}),
                    })
                  );

                  const finalAudioUrl = _.get(streamResponse, "speechUrl");
                  if (finalAudioUrl && messageForAudio?.id) {
                    await dispatch(
                      updateAITutorMessage({
                        variables: {
                          input: {
                            id: messageForAudio.id,
                            metadata: {
                              ..._.get(messageForAudio, "metadata", {}),
                              audioUrl: finalAudioUrl,
                              instructionsForTTSModel: TTSInstructions,
                            },
                          },
                        },
                      })
                    );
                  }
                  return;
                }

                const entry = ttsAudioRegistry.get(audioMessageId);
                if (!entry) return;

                // Create a SourceBuffer for MPEG audio format
                const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
                entry.sourceBuffer = sourceBuffer;

                // Queue to handle chunks when sourceBuffer is busy updating
                const chunkQueue = [];
                let hasAppendedData = false;
                let appendedChunkCount = 0;

                // Process queued audio chunks sequentially
                // SourceBuffer can only handle one append at a time
                const processQueue = () => {
                  // Check if still mounted before processing chunks
                  if (!isMountedRef?.current) {
                    audioEl.pause();
                    audioEl.muted = true;
                    return;
                  }

                  if (
                    chunkQueue.length > 0 &&
                    !sourceBuffer.updating &&
                    mediaSource.readyState === "open"
                  ) {
                    const nextChunk = chunkQueue.shift();
                    try {
                      sourceBuffer.appendBuffer(nextChunk);
                      hasAppendedData = true;
                      appendedChunkCount++;

                      // Start playing after 2 chunks are appended (similar to useTextToSpeech)
                      // This provides a buffer to prevent stuttering during playback
                      if (appendedChunkCount >= 2 && !entry.isReady) {
                        entry.isReady = true;
                      }
                    } catch {
                      entry.hasError = true;
                    }
                  }
                };

                // Continue processing queue after each buffer update completes
                sourceBuffer.addEventListener("updateend", processQueue);

                // Track errors during buffer operations
                sourceBuffer.addEventListener("error", () => {
                  entry.hasError = true;
                });

                // Track position in the audio stream for incremental processing
                let streamChunkIndex = 0;

                // Initiate TTS API call with streaming support
                const streamResponse = await dispatch(
                  getTextToSpeechAudio({
                    inputText: cleanedContent,
                    entityType: "AI_TUTOR_MESSAGE",
                    entityId: audioMessageId,
                    model: AI_TUTOR_TTS_MODEL,
                    ...(!_.isEmpty(TTSInstructions)
                      ? { instructions: TTSInstructions }
                      : {}),
                    setRequestController: controller => {
                      cancelTTSRequest = () => {
                        controller.abort();
                        const ttEntry = ttsAudioRegistry.get(audioMessageId);
                        if (ttEntry) ttEntry.isCancelled = true;
                      };
                    },
                    // Handle incoming audio chunks during streaming
                    onDownloadProgress: audioStream => {
                      // Check if still mounted before processing chunks
                      if (!isMountedRef?.current) {
                        audioEl.pause();
                        audioEl.muted = true;
                        return;
                      }

                      // audioStream is an array of parsed response items
                      const len = audioStream?.length;

                      // Process new chunks that haven't been handled yet
                      while (streamChunkIndex < len) {
                        const chunk = audioStream?.[streamChunkIndex]?.chunk;

                        // Convert raw chunk data to typed array and queue for playback
                        if (
                          !_.isNil(chunk) &&
                          typeof chunk?.data === "object"
                        ) {
                          const typedArray = new Uint8Array(chunk.data);
                          chunkQueue.push(typedArray.buffer);

                          // Try to process immediately if not busy
                          if (!sourceBuffer.updating) {
                            processQueue();
                          }
                        }

                        streamChunkIndex++;
                      }
                    },
                  })
                );

                // Wait for all queued chunks to be appended before ending stream
                // This ensures all audio data is processed before closing the MediaSource
                const waitForBufferCompletion = () => {
                  return new Promise(resolve => {
                    const checkCompletion = () => {
                      if (
                        chunkQueue.length === 0 &&
                        !sourceBuffer.updating &&
                        mediaSource.readyState === "open"
                      ) {
                        resolve();
                      } else if (mediaSource.readyState !== "open") {
                        resolve();
                      } else {
                        setTimeout(checkCompletion, 50);
                      }
                    };
                    checkCompletion();
                  });
                };

                await waitForBufferCompletion();

                // Only call endOfStream if component is still mounted and data was appended
                if (isMountedRef?.current) {
                  if (
                    hasAppendedData &&
                    !entry.hasError &&
                    mediaSource.readyState === "open"
                  ) {
                    try {
                      mediaSource.endOfStream();
                    } catch {
                      entry.hasError = true;
                    }
                  } else if (
                    !hasAppendedData &&
                    mediaSource.readyState === "open"
                  ) {
                    // No data was appended, mark as error
                    entry.hasError = true;
                  }
                }

                // Get the final audio URL from the TTS response for persistence
                const finalAudioUrl = _.get(streamResponse, "speechUrl");

                // Skip saving audio URL if the TTS request was cancelled by the user
                const isTTSCancelled = ttsAudioRegistry.get(audioMessageId)
                  ?.isCancelled;

                // Save the audio URL to the message metadata for future playback
                // This happens regardless of mount status, but not when cancelled
                if (finalAudioUrl && messageForAudio?.id && !isTTSCancelled) {
                  await dispatch(
                    updateAITutorMessage({
                      variables: {
                        input: {
                          id: messageForAudio.id,
                          metadata: {
                            ..._.get(messageForAudio, "metadata", {}),
                            audioUrl: finalAudioUrl,
                            instructionsForTTSModel: TTSInstructions,
                          },
                        },
                      },
                    })
                  );
                }

                audioEl.addEventListener(
                  "ended",
                  () => {
                    entry.hasEnded = true;
                    if (isMountedRef?.current) {
                      setCurrentlyAudioPlayingForItemId?.(null);
                    }
                  },
                  { once: true }
                );
              });
            }
          }

          // Save transcripts for user audio messages (speech-to-text results)
          // This enables text-based review of audio conversations
          _saveMessageTranscripts({
            messages: createBulkAiMessagesResponse,
            entityId,
            entitySubType: _.get(conversation, "entitySubType", entityType),
            dispatch,
            userMessageTranscriptsByMainModel,
          });
        } else {
          updateCurrentAIState(selectedTab, "");
        }
      }
    } catch (e) {
      if (e) updateCurrentAIState(selectedTab, "");
    }
  };
};

export const createOpeningMessage = params => {
  const { leId, selectedPrompt, setShowLoader, conversationId } = params;

  return async (dispatch, getState) => {
    const userAcademicYearId = getSelectedAcademicYearId(getState());

    const assessmentDetails = getAssessmentDetailsFromCache({
      id: leId,
      collaboratorsFilter: { userAcademicYearId },
    });

    const openingMessage = _.trim(
      _.find(assessmentDetails?.allFields, {
        uid: "openingMessage",
      })?.value
    );

    let attachments;

    if (getIsAITutorModalitiesEnabled()) {
      const openingMessageAttachmentGroups = _.get(
        _.find(assessmentDetails?.allFields, {
          uid: "openingMessageResource",
        }),
        "resolvedMinimalTree.attachmentGroups",
        []
      );

      const attachmentIds = _.chain(openingMessageAttachmentGroups)
        .flatMap(group => group?.attachments)
        .map("id")
        .compact()
        .value();

      attachments = await getMultiAttachments(attachmentIds);

      attachments = _.map(attachments, attachment => {
        const base = _.omit(attachment, ["id", "__typename"]);

        const toddleDoc = _.isEmpty(attachment.toddleDoc?.value)
          ? undefined
          : _.omit(attachment.toddleDoc, ["id", "__typename"]);

        const modalityFile = _.isEmpty(attachment.modalityFile?.content)
          ? undefined
          : _.omit(attachment.modalityFile, ["id", "__typename"]);

        const metadata =
          attachment?.type !== "LINK"
            ? { ..._.get(attachment, "metadata", {}), version: 1 }
            : _.get(attachment, "metadata", {});

        return {
          ...base,
          toddleDoc,
          modalityFile,
          createdBy: getUserInfo().id,
          metadata,
        };
      });
    }

    const libraryPromptOpeningMessage = _.trim(
      _.find(selectedPrompt?.fields, {
        uid: "openingMessage",
      })?.value
    );

    const content = libraryPromptOpeningMessage || openingMessage;
    const metadata = { isOpeningMessage: true };

    const refetchQueries = [];

    if (conversationId) {
      refetchQueries.push({
        query: getAITutorConversationQuery,
        variables: {
          id: conversationId,
        },
      });
    }

    // add the opening msg to the conversation
    if (!_.isEmpty(content)) {
      let _content = content;

      if (getIsAITutorEnhancementsV2Enabled()) {
        _content = replaceImgWithInlineLatex({
          query: _content,
          preserveHtml: true,
        });

        const tutorCommunicationMode = getAssessmentFieldValue({
          fields: assessmentDetails?.allFields,
          uid: "tutorCommunicationMode",
        });

        const isSpeakingOnly = tutorCommunicationMode === "speaking_only";

        if (isSpeakingOnly) {
          const taskCreationConversation = _.find(
            assessmentDetails?.aiConversation?.edges,
            ({ node }) => node?.entitySubType === "TASK_CREATION"
          )?.node;

          const instructionsForTTSModel = _.get(
            taskCreationConversation,
            "metadata.instructionsForTTSModel",
            ""
          );

          const streamResponse = await dispatch(
            getTextToSpeechAudio({
              inputText: content,
              entityType: "AI_TUTOR_MESSAGE",
              model: AI_TUTOR_TTS_MODEL,
              ...(!_.isEmpty(instructionsForTTSModel)
                ? { instructions: instructionsForTTSModel }
                : {}),
            })
          );

          const audioUrl = _.get(streamResponse, "speechUrl");
          if (audioUrl) _.set(metadata, "audioUrl", audioUrl);
        }
      }

      await dispatch(
        createAITutorMessage({
          variables: {
            input: {
              conversationId,
              authorType: "assistant",
              content: _content,
              metadata,
              attachments,
            },
          },
          writeToCache: false,
          refetchQueries,
        })
      );

      setShowLoader?.(false);
    }
  };
};

export const generateProficiencyLevelConversation = params => {
  const {
    course,
    entityId,
    entityType = "ai_tutor_chat",
    gradeId,
    conversationId,
    setShowLoader,
    updateCurrentAIState,
    selectedTab,
    updateIsAnalysing,
    learningCourseId,
  } = params;

  return async (dispatch, getState) => {
    updateCurrentAIState(selectedTab, STREAMING);

    let conversation = getAITutorConversationFromCache({
      id: conversationId,
    });

    if (_.isEmpty(conversation) && conversationId) {
      conversation = await getAITutorConversation({ id: conversationId });
    }

    // context calculation
    const grades =
      _.get(course?.primaryGrade, "name") ||
      _.join(_.map(course?.grades, "name"), ", ");

    const context = {
      inputText: "",
      grades,
    };

    const userInfo = getUserInfo();
    const createdBy = {
      ...getUserBasicDetailsFromCache({
        id: userInfo.id,
        type: userInfo.userEntityType,
      }),
      type: userInfo.user_type,
      schoolTenures: [],
    };

    const createdAt = moment().toISOString();

    const response = await dispatch(
      getAIResponse({
        variables: {
          input: {
            context,
            entityId,
            entityType,
            curriculumProgramId: getSelectedCurriculumId(getState()),
            metadata: {
              gradeId,
              conversationId,
              learningCourseId,
            },
            action: "proficiency_level_sample_conv",
            type: "BRAINSTORM",
          },
        },
        onDownloadProgress: params => {
          const { content: rawContent, usageMetricsId, latestChunkStatus } =
            params || {};

          if (latestChunkStatus === "TOOL_INDICATION") {
            updateIsAnalysing(selectedTab, true);
          } else {
            updateIsAnalysing(selectedTab, false);
            updateCurrentAIState(selectedTab, STREAMING);
          }

          // Clean LaTeX content by removing commas between curly braces
          const content = removeCommasBetweenBraces(rawContent);

          const messages = parseProficiencyLevelConversation(content);

          if (!_.isEmpty(messages)) {
            setShowLoader?.(false);
          }

          const messageNodes = _.map(messages, message => {
            const { id, authorType, content } = message || {};

            return {
              node: {
                id: `${conversationId}-${id}`, // do not change being used in AITutorChat.js to determine isCurrentAudioPlaying
                role: null,
                severity: null,
                authorType,
                content,
                metadata: {},
                createdBy,
                attachments: null,
                votes: {
                  myVote: null,
                  __typename: "VoteConnection",
                },
                createdAt,
                usageMetricsId: usageMetricsId || null,
                parentMessage: {
                  id: null,
                  __typename: "AiMessage",
                },
                __typename: "AiMessage",
              },
              __typename: "AiMessageEdge",
            };
          });

          const dataToSend = update(conversation, {
            messages: {
              edges: {
                $set: messageNodes,
              },
              totalCount: {
                $set: _.size(messageNodes),
              },
            },
          });

          if (!_.isEmpty(messageNodes)) {
            writeAITutorConversationInCache({
              variables: {
                id: conversationId,
              },
              data: {
                node: dataToSend,
              },
            });
          }
        },
      })
    );

    const { content: rawResponse, usageMetricsId, model, temperature } =
      response || {};

    if (!_.isEmpty(rawResponse)) {
      // Clean LaTeX content by removing commas between curly braces
      const content = removeCommasBetweenBraces(rawResponse);

      const messages = _.map(
        parseProficiencyLevelConversation(content, false),
        message => _.pick(message, ["content", "authorType"])
      );

      _.set(messages, "0.metadata", { model, temperature });
      _.set(messages, "0.usageMetricsId", usageMetricsId);

      updateCurrentAIState(selectedTab, "");

      await dispatch(
        createBulkAiMessages({ input: { conversationId, messages } })
      );
    }
  };
};

export const evaluateAITutorConversation = params => {
  const {
    course,
    entityId,
    entityType = "ai_tutor_evaluation",
    gradeId,
    aiConversationId,
    assessmentDetails,
    setCurrentAIState,
    shouldCreateEvaluationMessage,
    learningCourseId,
  } = params;

  return async (dispatch, getState) => {
    // context calculation

    setCurrentAIState?.(GENERATING_EVALUATION);

    const grades =
      _.get(course?.primaryGrade, "name") ||
      _.join(_.map(course?.grades, "name"), ", ") ||
      _.join(_.map(assessmentDetails?.courseGrades, "name"), ", ");

    const _gradeId =
      gradeId ||
      _.get(course, "primaryGrade.id") ||
      _.get(course?.grades, "0.id") ||
      _.get(assessmentDetails, "grades.0.id") ||
      _.get(assessmentDetails?.courseGrades, "0.id");

    const aiAnalysisCriteria = getAIAnalysisItem(assessmentDetails);
    const aiAnalysisIndicators = getAIAnalysisItem(
      assessmentDetails,
      "indicators"
    );

    const context = {
      inputText: "",
      grades,
    };

    const id = generateRandomId();

    const analysisToolEvaluationId = generateRandomId();
    const toolId = generateRandomId();

    const strengthId = generateRandomId();
    const areasOfImprovementId = generateRandomId();
    const nextStepsId = generateRandomId();

    const conversation = getAITutorConversationFromCache({
      id: aiConversationId,
    });

    const isAITutorEnhancementsV2Enabled = getIsAITutorEnhancementsV2Enabled();

    const input = {
      context,
      entityId,
      entityType,
      curriculumProgramId: getSelectedCurriculumId(getState()),
      metadata: {
        gradeId: _gradeId,
        conversationId: aiConversationId,
        assessmentId: assessmentDetails?.id,
        learningCourseId,
      },
    };

    if (isAITutorEnhancementsV2Enabled) {
      input.type = "EVALUATE_STUDENT_CONVERSATION";
      input.metadata.skipSemanticSearch = true;
    } else {
      input.action = "ai_chat_evaluation_context";
      input.type = "BRAINSTORM";
    }

    const createdAt = moment().toISOString();

    const response = await dispatch(
      getAIResponse({
        variables: { input },
        getCancelToken: token => (cancelRequest = token),
        onDownloadProgress: params => {
          const { content: rawContent, usageMetricsId } = params || {};

          // Clean LaTeX content by removing commas between curly braces
          const content = decodeHTMLEntities(
            removeCommasBetweenBraces(rawContent)
          );

          const createAiMessageNode = {
            node: {
              id,
              role: null,
              severity: null,
              authorType: "aiTutorEvaluation",
              content,
              metadata: {},
              createdBy: null,
              attachments: null,
              votes: {
                myVote: null,
                __typename: "VoteConnection",
              },
              createdAt,
              usageMetricsId: usageMetricsId || null,
              parentMessage: {
                id: null,
                __typename: "AiMessage",
              },
              __typename: "AiMessage",
            },
            __typename: "AiMessageEdge",
          };

          const analysisToolEvaluationNode = [
            {
              id: _.get(
                conversation,
                "analysisToolEvaluation.0.id",
                analysisToolEvaluationId
              ),
              toolId: _.get(
                conversation,
                "analysisToolEvaluation.0.toolId",
                toolId
              ),
              toolType: "ANALYSIS",
              toolResponses: [],
              __typename: "ToolResponseEdge",
            },
          ];

          const evaluationLevels = extractEvaluationAnalysisLevels(content);

          const { proficiency, strengths, areasOfImprovement, nextSteps } =
            evaluationLevels || {};

          const rowId = findEvaluatedCriteriaId(
            aiAnalysisCriteria,
            proficiency
          );

          if (rowId) {
            if (!_.isEmpty(strengths)) {
              analysisToolEvaluationNode[0].toolResponses.push({
                id: strengthId,
                rowId,
                columnId: aiAnalysisIndicators[0].id,
                subjectiveResponse: strengths,
                creatorType: "AI",
                isDeleted: false,
                evaluationJustification: null,
                __typename: "AssessmentToolResponse",
              });
            }

            if (!_.isEmpty(areasOfImprovement)) {
              analysisToolEvaluationNode[0].toolResponses.push({
                id: areasOfImprovementId,
                rowId,
                columnId: aiAnalysisIndicators[1].id,
                subjectiveResponse: areasOfImprovement,
                creatorType: "AI",
                isDeleted: false,
                evaluationJustification: null,
                __typename: "AssessmentToolResponse",
              });
            }

            if (!_.isEmpty(nextSteps)) {
              analysisToolEvaluationNode[0].toolResponses.push({
                id: nextStepsId,
                rowId,
                columnId: aiAnalysisIndicators[2].id,
                subjectiveResponse: nextSteps,
                creatorType: "AI",
                isDeleted: false,
                evaluationJustification: null,
                __typename: "AssessmentToolResponse",
              });
            }
          }

          let dataToSend = conversation;

          if (shouldCreateEvaluationMessage) {
            dataToSend = update(conversation, {
              messages: {
                edges: {
                  $unshift: [createAiMessageNode],
                },
                totalCount: {
                  $set: _.get(conversation, "messages.totalCount", 0) + 1,
                },
              },
              analysisToolEvaluation: {
                $set: analysisToolEvaluationNode,
              },
            });
          } else {
            dataToSend = update(conversation, {
              analysisToolEvaluation: {
                $set: analysisToolEvaluationNode,
              },
            });
          }

          const justificationOpenTagRegex = /<justification>/;
          const justificationCloseTagRegex = /<\/justification>/;

          const hasOpenTag = content && justificationOpenTagRegex.test(content);
          const hasCloseTag =
            content && justificationCloseTagRegex.test(content);

          // Go inside if content doesn't have justification tags at all OR if it has complete justification (both open and close tags)
          if (
            !_.isEmpty(content) &&
            (!hasOpenTag || (hasOpenTag && hasCloseTag))
          ) {
            writeAITutorConversationInCache({
              variables: { id: aiConversationId },
              data: { node: dataToSend },
            });

            setCurrentAIState?.(STREAMING);
          }
        },
      })
    );

    const {
      content: rawResponse,
      usageMetricsId,
      model,
      temperature,
      intentUsageMetricsId,
    } = response || {};

    if (!_.isEmpty(rawResponse)) {
      // Clean LaTeX content by removing commas between curly braces
      const content = removeCommasBetweenBraces(rawResponse);

      const conversation = getAITutorConversationFromCache({
        id: aiConversationId,
      });

      const evaluationLevels = extractEvaluationAnalysisLevels(content);
      const {
        proficiency,
        strengths,
        areasOfImprovement,
        nextSteps,
        strengthsHeader,
        areasOfImprovementHeader,
        nextStepsHeader,
      } = evaluationLevels || {};

      const rowId = findEvaluatedCriteriaId(aiAnalysisCriteria, proficiency);

      if (rowId) {
        const assessmentToolId = _.get(
          _.find(
            assessmentDetails?.assessmentTools?.edges,
            ({ node }) => node?.assessmentToolType === "ANALYSIS"
          ),
          "node.assessmentToolItem.id"
        );

        const userInfo = getUserInfo();

        const commonInput = {
          parentId: aiConversationId,
          parentType: "AI_CONVERSATION",
          type: "ANALYSIS",
          assessmentToolId,
          rowId,
          creatorType: "AI",
          createdBy: userInfo.id,
          studentAssessmentEvaluationId: 123,
        };

        const bulkAssessmentToolResponseInput = [];
        const updateAnalysisToolDetailsInput = [];

        if (!_.isEmpty(strengths)) {
          bulkAssessmentToolResponseInput.push({
            ...commonInput,
            columnId: aiAnalysisIndicators[0].id,
            subjectiveResponse: strengths,
          });
        }

        if (
          !_.isEmpty(strengthsHeader) &&
          strengthsHeader !== aiAnalysisIndicators[0].label
        ) {
          updateAnalysisToolDetailsInput.push({
            id: aiAnalysisIndicators[0].id,
            label: strengthsHeader,
            type: "INDICATOR",
          });
        }

        if (!_.isEmpty(areasOfImprovement)) {
          bulkAssessmentToolResponseInput.push({
            ...commonInput,
            columnId: aiAnalysisIndicators[1].id,
            subjectiveResponse: areasOfImprovement,
          });
        }

        if (
          !_.isEmpty(areasOfImprovementHeader) &&
          areasOfImprovementHeader !== aiAnalysisIndicators[1].label
        ) {
          updateAnalysisToolDetailsInput.push({
            id: aiAnalysisIndicators[1].id,
            label: areasOfImprovementHeader,
            type: "INDICATOR",
          });
        }

        if (!_.isEmpty(nextSteps)) {
          bulkAssessmentToolResponseInput.push({
            ...commonInput,
            columnId: aiAnalysisIndicators[2].id,
            subjectiveResponse: nextSteps,
          });
        }

        if (
          !_.isEmpty(nextStepsHeader) &&
          nextStepsHeader !== aiAnalysisIndicators[2].label
        ) {
          updateAnalysisToolDetailsInput.push({
            id: aiAnalysisIndicators[2].id,
            label: nextStepsHeader,
            type: "INDICATOR",
          });
        }

        if (!_.isEmpty(bulkAssessmentToolResponseInput)) {
          await dispatch(
            bulkHandleAssessmentToolResponse({
              input: bulkAssessmentToolResponseInput,
            })
          );
        }

        if (
          getIsAITutorEnhancementsV2Enabled() &&
          !_.isEmpty(updateAnalysisToolDetailsInput)
        ) {
          dispatch(
            updateAnalysisToolDetails({
              input: {
                updateAnalysisToolDetails: updateAnalysisToolDetailsInput,
              },
            })
          );
        }

        if (shouldCreateEvaluationMessage) {
          // Generate the AI message with authorType as aiTutorEvaluation
          await dispatch(
            createAITutorMessage({
              variables: {
                input: {
                  conversationId: aiConversationId,
                  authorType: "aiTutorEvaluation",
                  content,
                  usageMetricsId,
                  metadata: {},
                },
              },
              writeToCache: false,
            })
          );
        }

        await dispatch(
          updateAIConversation({
            input: {
              id: aiConversationId,
              metadata: {
                ...(conversation?.metadata || {}),
                usageMetricsId,
                model,
                temperature,
                intentUsageMetricsId,
              },
            },
          })
        );

        await getAIAnalysisScaleEvaluation({ id: aiConversationId });

        setCurrentAIState?.("");
        return true;
      }

      return false;
    }
    setCurrentAIState?.("");
    return false;
  };
};

export const createBulkAiMessages = ({ input, attachments = null }) => {
  return async dispatch => {
    const { conversationId, messages } = input;

    const conversation = getAITutorConversationFromCache({
      id: conversationId,
    });

    const userInfo = getUserInfo();
    const createdBy = {
      ...getUserBasicDetailsFromCache({
        id: userInfo.id,
        type: userInfo.userEntityType,
      }),
      type: userInfo.user_type,
      schoolTenures: [],
    };

    // Create optimistic responses for all messages
    const optimisticMessages = _.map(messages, message => ({
      id: generateRandomId(),
      role: null,
      severity: null,
      authorType: _.get(message, "authorType"),
      content: _.get(message, "content"),
      metadata: _.get(message, "metadata", {}),
      attachments,
      createdBy,
      votes: {
        myVote: null,
        __typename: "VoteConnection",
      },
      createdAt: moment().toISOString(),
      usageMetricsId: _.get(message, "usageMetricsId", null),
      parentMessage: {
        id: _.get(message, "parentMessageId", null),
        __typename: "AiMessage",
      },
      __typename: "AiMessage",
    }));

    try {
      return await client.mutate({
        mutation: createBulkAiMessagesMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            createBulkAiMessages: {
              isSuccess: true,
              response: optimisticMessages,
              __typename: "BulkAiMessagesResponse",
            },
          },
        },
        update: (
          cache,
          {
            data: {
              ai: { createBulkAiMessages },
            },
          }
        ) => {
          cache.updateQuery(
            {
              query: getAITutorConversationQuery,
              variables: {
                id: conversationId,
              },
            },
            data => {
              if (!data) return data;

              const nodes = _.map(createBulkAiMessages?.response, node => {
                return { node, __typename: "AiMessageEdge" };
              }).reverse();

              if (!_.isEmpty(nodes)) {
                // Filter out existing edges where node.id starts with "NEW_"
                const existingEdges = _.get(data, "node.messages.edges", []);
                const filteredEdges = _.filter(existingEdges, edge => {
                  const messageId = _.get(edge, "node.id", "");

                  return (
                    !_.startsWith(messageId, "NEW_") &&
                    !_.includes(messageId, "-") // ${conversationId}-${id} case in generateProficiencyLevelConversation
                  );
                });

                const updatedData = update(data, {
                  node: {
                    messages: {
                      edges: {
                        $set: [...nodes, ...filteredEdges],
                      },
                      totalCount: {
                        $set: filteredEdges.length + _.size(nodes),
                      },
                    },
                  },
                });

                return updatedData;
              }

              return data;
            }
          );
        },
      });
    } catch {
      // Revert cache on error
      cache.updateQuery(
        {
          query: getAITutorConversationQuery,
          variables: {
            id: conversationId,
          },
        },
        () => ({ node: conversation })
      );

      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

export const resetAITutorConversation = variables => {
  return async (dispatch, getState) => {
    try {
      const response = await client.mutate({
        mutation: resetAITutorConversationMutation,
        variables,
      });

      if (!_.get(response, "isSuccess", true)) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
        return;
      }

      return response;
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const onEndSession = params => {
  const {
    addActivityLog = true,
    course,
    entityId,
    aiConversationId,
    assessmentDetails,
    assignmentId,
    shouldCreateEvaluationMessage,
    updateCurrentAIState,
    learningCourseId,
  } = params || {};

  return async dispatch => {
    dispatch(updateEvaluationData({ showAIAnalysisLoader: true }));

    const userInfo = getUserInfo();
    const userId = userInfo.id;
    const userType = userInfo.user_type;
    const isStudent = userType === "student";

    const setCurrentAIState = aiAnalysisState => {
      // for play mode update local state, for student/student preview update global state
      // because for play mode loader is shown in the same component, but for student/student preview
      // loader is shown in another component (through redux)
      if (shouldCreateEvaluationMessage) {
        updateCurrentAIState(aiAnalysisState);
      } else {
        dispatch(updateEvaluationData({ aiAnalysisState }));
      }
    };

    const isSuccess = await dispatch(
      evaluateAITutorConversation({
        course,
        entityId,
        aiConversationId,
        assessmentDetails,
        setCurrentAIState,
        shouldCreateEvaluationMessage,
        learningCourseId,
      })
    );

    if (isSuccess && addActivityLog && isStudent && assignmentId) {
      await dispatch(
        addAssignmentActivity({
          assignmentId,
          activities: [
            {
              type: "STUDENT_ASSIGNMENT_ACTIVITY",
              itemType: "STUDENT_ASSIGNMENT_END_AI_CONVERSATION_SESSION",
            },
          ],
          studentIds: [userId],
        })
      );
    }

    dispatch(updateEvaluationData({ showAIAnalysisLoader: false }));
  };
};

export const updateAITutorMessage = ({ variables }) => {
  return async (dispatch, getState) => {
    try {
      return await client.mutate({
        mutation: updateAITutorMessageMutation,
        variables,
      });
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const createAITutorConversation = ({ input = {} }) => {
  return async (dispatch, getState) => {
    try {
      const userInfo = getUserInfo();
      const userType = userInfo.user_type;

      const {
        entityId,
        entityType = null,
        entitySubType = "TASK_CREATION",
      } = input;

      const response = await client.mutate({
        mutation: createAITutorConversationMutation,
        variables: { input },
        update: (
          cache,
          {
            data: {
              ai: { createAiConversation },
            },
          }
        ) => {
          if (userType === "staff") {
            const userAcademicYearId = getSelectedAcademicYearId(getState());
            const collaboratorsFilter = { userAcademicYearId };

            const conversationId = _.get(createAiConversation, "response.id");

            cache.updateQuery(
              {
                query: getAssessmentQuery,
                variables: {
                  id: entityId,
                  collaboratorsFilter,
                },
              },
              data => {
                return update(data, {
                  node: {
                    aiConversation: {
                      edges: {
                        $set: [
                          ..._.get(data, "node.aiConversation.edges", []),
                          {
                            node: {
                              id: conversationId,
                              entityType,
                              entitySubType,
                              metadata: null,
                              attachments: [],
                              analysisToolEvaluation: [],
                              messages: [],
                              __typename: "AiConversation",
                            },
                            __typename: "AiConversationEdge",
                          },
                        ],
                      },
                      __typename: { $set: "AiConversationConnection" },
                    },
                  },
                });
              }
            );
          }

          if (userType === "student") {
            const studentAssignmentVariables = {
              id: entityId,
              showOnlySubmitted: false,
              userFilter: getToolResponseUserFilter(userType, false),
              entityEventsFilter: {
                orderBy: "EVENT_TIME",
                orderByDir: "ASC",
              },
            };

            const studentAssignmentDetailsQuery = {
              query: getStudentAssignmentDetailsQuery,
              variables: studentAssignmentVariables,
            };

            const studentAssignmentQuery = {
              query: getStudentAssignmentQuery,
              variables: {
                id: entityId,
                userFilter: "ALL",
                entityEventsFilter: {
                  orderBy: "EVENT_TIME",
                  orderByDir: "ASC",
                },
              },
            };

            cache.updateQuery(
              getIsStudentAssignmentRevampEnabled()
                ? studentAssignmentQuery
                : studentAssignmentDetailsQuery,
              data => {
                const updatedData = update(data, {
                  node: {
                    aiConversation: {
                      $set: {
                        id: _.get(createAiConversation, "response.id"),
                        metadata: {},
                        messages: {
                          totalCount: _.get(
                            createAiConversation,
                            "response.messages.totalCount",
                            0
                          ),
                        },
                        analysisToolEvaluation: [],
                      },
                    },
                    aiConversations: {
                      edges: {
                        $set: [
                          ...(data.node.aiConversations?.edges || []),
                          {
                            node: {
                              id: _.get(createAiConversation, "response.id"),
                              entityType: _.get(
                                createAiConversation,
                                "response.entityType"
                              ),
                              entitySubType: _.get(
                                createAiConversation,
                                "response.entitySubType"
                              ),
                              messages: {
                                edges: _.get(
                                  createAiConversation,
                                  "response.messages.edges",
                                  []
                                ),
                                totalCount: _.get(
                                  createAiConversation,
                                  "response.messages.totalCount",
                                  0
                                ),
                              },
                              analysisToolEvaluation: _.get(
                                createAiConversation,
                                "response.analysisToolEvaluation",
                                []
                              ),
                            },
                          },
                        ],
                      },
                    },
                  },
                });

                return updatedData;
              }
            );
          }
        },
      });

      return _.get(response, "data.ai.createAiConversation", {});
    } catch (error) {
      // no need to show error toast
      Sentry.captureMessage(`AI tutor conversation creation error`, {
        level: "error",
        extra: {
          input,
          error,
        },
      });
      return {};
    }
  };
};

export const updateAIMessage = variables => {
  return async dispatch => {
    try {
      return await client.mutate({
        mutation: updateAIMessageMutation,
        variables,
      });
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};
